import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { ArrowLeft, LogOut } from "lucide-react";
import { toast } from "sonner";
import { useSiteBranding } from "../contexts/SiteBrandingContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EditAd = ({ user, onLogout }) => {
  const { branding } = useSiteBranding();
  const navigate = useNavigate();
  const { adId } = useParams();
  const [loading, setLoading] = useState(false);
  const [fetchingAd, setFetchingAd] = useState(true);
  const [categories, setCategories] = useState([]);
  const [serviceAreas, setServiceAreas] = useState([]);
  const [subcategories, setSubcategories] = useState([]);

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    location: "",
    contact_phone: "",
    contact_roadname: "",
    website_link: "",
    general_category: "",
    subcategory: [], // Changed to array for multi-select
    service_area: [], // Changed to array for multi-select
    image_url: "",
    discord_link: "",
    instagram_link: "",
    facebook_link: "",
    telegram_link: "",
    whatsapp_link: "",
    linkedin_link: ""
  });
  const [uploading, setUploading] = useState(false);
  const [imageFile, setImageFile] = useState(null);

  useEffect(() => {
    fetchCategories();
    fetchAdData();
  }, []);

  useEffect(() => {
    if (formData.general_category) {
      const category = categories.find(cat => cat.name === formData.general_category);
      setSubcategories(category?.subcategories || []);
    } else {
      setSubcategories([]);
    }
  }, [formData.general_category, categories]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data.categories);
      setServiceAreas(response.data.service_areas || []);
    } catch (error) {
      toast.error("Failed to fetch categories");
    }
  };

  const fetchAdData = async () => {
    setFetchingAd(true);
    try {
      const response = await axios.get(`${API}/ads/${adId}`);
      setFormData({
        title: response.data.title || "",
        description: response.data.description || "",
        location: response.data.location || "",
        contact_phone: response.data.contact_phone || "",
        contact_roadname: response.data.contact_roadname || "",
        website_link: response.data.website_link || "",
        general_category: response.data.general_category || "",
        subcategory: response.data.subcategory || [], // Handle as array
        service_area: response.data.service_area || [], // Handle as array
        image_url: response.data.image_url || "",
        discord_link: response.data.discord_link || "",
        instagram_link: response.data.instagram_link || "",
        facebook_link: response.data.facebook_link || "",
        telegram_link: response.data.telegram_link || "",
        whatsapp_link: response.data.whatsapp_link || "",
        linkedin_link: response.data.linkedin_link || ""
      });
    } catch (error) {
      toast.error("Failed to fetch ad data");
      navigate("/");
    } finally {
      setFetchingAd(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error("Please select an image file");
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image size must be less than 5MB");
      return;
    }

    setImageFile(file);
    setUploading(true);

    try {
      const token = localStorage.getItem("token");
      const formDataUpload = new FormData();
      formDataUpload.append("file", file);

      const response = await axios.post(`${API}/upload-image`, formDataUpload, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data"
        }
      });

      // Add cache-busting and ensure state is updated
      const imageUrl = response.data.image_url;
      setFormData(prev => ({ ...prev, image_url: imageUrl }));
      
      // Force a slight delay to ensure state is updated
      await new Promise(resolve => setTimeout(resolve, 100));
      
      toast.success("Image uploaded successfully!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload image");
      setImageFile(null);
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveImage = () => {
    setImageFile(null);
    setFormData(prev => ({ ...prev, image_url: "" }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem("token");
      await axios.put(`${API}/ads/${adId}`, formData, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      toast.success("Ad updated successfully!");
      navigate("/");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update ad");
    } finally {
      setLoading(false);
    }
  };

  if (fetchingAd) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block w-12 h-12 border-4 border-black border-t-transparent rounded-full animate-spin"></div>
          <p className="mt-4 text-gray-600">Loading ad data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-200">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{ backgroundColor: branding.logo_bg_color || '#000000' }}>
                {branding.logo_url ? (
                  <img 
                    src={`${BACKEND_URL}${branding.logo_url}`} 
                    alt="Logo" 
                    className="w-full h-full object-contain rounded-lg"
                  />
                ) : (
                  <span className="text-white text-xl font-bold">ESMC</span>
                )}
              </div>
              <h1 className="text-2xl font-bold text-black">Edit Ad</h1>
            </div>

            <div className="flex items-center space-x-4">
              <span className="text-gray-600 text-sm sm:text-base">Welcome, {user?.name}</span>
              <Button
                variant="outline"
                onClick={onLogout}
                data-testid="logout-button"
                className="border-2 border-gray-300 hover:bg-gray-100"
              >
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Button
          variant="ghost"
          onClick={() => navigate("/")}
          data-testid="back-button"
          className="mb-6 hover:bg-gray-100"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Dashboard
        </Button>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="title" className="text-sm font-medium text-gray-700">
                Ad Title *
              </Label>
              <Input
                id="title"
                data-testid="title-input"
                value={formData.title}
                onChange={(e) => handleChange("title", e.target.value)}
                placeholder="e.g., Professional Plumbing Services"
                required
                className="h-12 border-2 border-gray-200 focus:border-black transition-colors"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description" className="text-sm font-medium text-gray-700">
                Description
              </Label>
              <textarea
                id="description"
                data-testid="description-input"
                value={formData.description}
                onChange={(e) => handleChange("description", e.target.value)}
                placeholder="Describe your service in detail..."
                rows={6}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-black focus:outline-none transition-colors resize-vertical"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label className="text-sm font-medium text-gray-700">
                  General Category
                </Label>
                <Select
                  value={formData.general_category}
                  onValueChange={(value) => handleChange("general_category", value)}
                >
                  <SelectTrigger data-testid="general-category-select" className="h-12 border-2 border-gray-200">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((cat) => (
                      <SelectItem key={cat.name} value={cat.name}>
                        {cat.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-medium text-gray-700">
                  Subcategory
                </Label>
                <Select
                  value={formData.subcategory.length > 0 ? "multi-selected" : ""}
                  onValueChange={(value) => {
                    if (value === "clear-all") {
                      handleChange("subcategory", []);
                    } else if (value !== "multi-selected") {
                      // Toggle selection
                      const currentSubcats = formData.subcategory || [];
                      if (currentSubcats.includes(value)) {
                        handleChange("subcategory", currentSubcats.filter(sub => sub !== value));
                      } else {
                        handleChange("subcategory", [...currentSubcats, value]);
                      }
                    }
                  }}
                  disabled={!formData.general_category}
                >
                  <SelectTrigger data-testid="subcategory-select" className="h-12 border-2 border-gray-200">
                    <SelectValue placeholder="Select subcategory">
                      {(() => {
                        const validSubcats = (formData.subcategory || []).filter(sub => sub && sub.trim());
                        return validSubcats.length > 0 
                          ? `${validSubcats.length} selected` 
                          : "Select subcategory";
                      })()}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent className="max-h-[300px] overflow-y-auto">
                    {(formData.subcategory || []).filter(sub => sub && sub.trim()).length > 0 && (
                      <SelectItem value="clear-all" className="text-red-600 font-medium">
                        ✕ Clear All
                      </SelectItem>
                    )}
                    {subcategories.map((sub) => (
                      <SelectItem 
                        key={sub} 
                        value={sub}
                        className={`cursor-pointer ${formData.subcategory.includes(sub) ? "bg-gray-100 font-medium" : ""}`}
                      >
                        <div className="flex items-center gap-2 w-full pointer-events-none">
                          <div className={`w-4 h-4 border-2 rounded flex items-center justify-center flex-shrink-0 ${formData.subcategory.includes(sub) ? "bg-black border-black" : "border-gray-300"}`}>
                            {formData.subcategory.includes(sub) && (
                              <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                              </svg>
                            )}
                          </div>
                          <span>{sub}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="contact_phone" className="text-sm font-medium text-gray-700">
                  Contact Phone
                </Label>
                <Input
                  id="contact_phone"
                  data-testid="contact-phone-input"
                  value={formData.contact_phone}
                  onChange={(e) => handleChange("contact_phone", e.target.value)}
                  placeholder="+1 234 567 8900"
                  className="h-12 border-2 border-gray-200 focus:border-black transition-colors"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="contact_roadname" className="text-sm font-medium text-gray-700">
                  Contact Road Name
                </Label>
                <Input
                  id="contact_roadname"
                  data-testid="contact-roadname-input"
                  value={formData.contact_roadname}
                  onChange={(e) => handleChange("contact_roadname", e.target.value)}
                  placeholder="e.g., 123 Main Street"
                  className="h-12 border-2 border-gray-200 focus:border-black transition-colors"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-medium text-gray-700">
                Service Area
              </Label>
              <Select
                value={formData.service_area.length > 0 ? "multi-selected" : ""}
                onValueChange={(value) => {
                  if (value === "clear-all") {
                    handleChange("service_area", []);
                  } else if (value !== "multi-selected") {
                    // Toggle selection
                    const currentAreas = formData.service_area || [];
                    if (currentAreas.includes(value)) {
                      handleChange("service_area", currentAreas.filter(area => area !== value));
                    } else {
                      handleChange("service_area", [...currentAreas, value]);
                    }
                  }
                }}
              >
                <SelectTrigger data-testid="service-area-select" className="h-12 border-2 border-gray-200">
                  <SelectValue placeholder="Select service area">
                    {(() => {
                      const validAreas = (formData.service_area || []).filter(area => area && area.trim());
                      return validAreas.length > 0 
                        ? `${validAreas.length} selected` 
                        : "Select service area";
                    })()}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent className="max-h-[300px] overflow-y-auto">
                  {(formData.service_area || []).filter(area => area && area.trim()).length > 0 && (
                    <SelectItem value="clear-all" className="text-red-600 font-medium">
                      ✕ Clear All
                    </SelectItem>
                  )}
                  {serviceAreas.map((area) => (
                    <SelectItem 
                      key={area} 
                      value={area}
                      className={`cursor-pointer ${formData.service_area.includes(area) ? "bg-gray-100 font-medium" : ""}`}
                    >
                      <div className="flex items-center gap-2 w-full pointer-events-none">
                        <div className={`w-4 h-4 border-2 rounded flex items-center justify-center flex-shrink-0 ${formData.service_area.includes(area) ? "bg-black border-black" : "border-gray-300"}`}>
                          {formData.service_area.includes(area) && (
                            <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                        </div>
                        <span>{area}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="location" className="text-sm font-medium text-gray-700">
                Location
              </Label>
              <Input
                id="location"
                data-testid="location-input"
                value={formData.location}
                onChange={(e) => handleChange("location", e.target.value)}
                placeholder="e.g., New York, NY"
                className="h-12 border-2 border-gray-200 focus:border-black transition-colors"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="website_link" className="text-sm font-medium text-gray-700">
                Website Link
              </Label>
              <Input
                id="website_link"
                data-testid="website-link-input"
                value={formData.website_link}
                onChange={(e) => handleChange("website_link", e.target.value)}
                placeholder="https://example.com"
                className="h-12 border-2 border-gray-200 focus:border-black transition-colors"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-medium text-gray-700">
                Social Media Links (Optional)
              </Label>
              <div className="space-y-3">
                <div>
                  <Label htmlFor="discord_link" className="text-xs text-gray-600">
                    Discord
                  </Label>
                  <Input
                    id="discord_link"
                    value={formData.discord_link}
                    onChange={(e) => handleChange("discord_link", e.target.value)}
                    placeholder="https://discord.gg/..."
                    className="h-12 border-2 border-gray-200 focus:border-black transition-colors"
                  />
                </div>

                <div>
                  <Label htmlFor="instagram_link" className="text-xs text-gray-600">
                    Instagram
                  </Label>
                  <Input
                    id="instagram_link"
                    value={formData.instagram_link}
                    onChange={(e) => handleChange("instagram_link", e.target.value)}
                    placeholder="https://instagram.com/..."
                    className="h-12 border-2 border-gray-200 focus:border-black transition-colors"
                  />
                </div>

                <div>
                  <Label htmlFor="facebook_link" className="text-xs text-gray-600">
                    Facebook
                  </Label>
                  <Input
                    id="facebook_link"
                    value={formData.facebook_link}
                    onChange={(e) => handleChange("facebook_link", e.target.value)}
                    placeholder="https://facebook.com/..."
                    className="h-12 border-2 border-gray-200 focus:border-black transition-colors"
                  />
                </div>

                <div>
                  <Label htmlFor="telegram_link" className="text-xs text-gray-600">
                    Telegram
                  </Label>
                  <Input
                    id="telegram_link"
                    value={formData.telegram_link}
                    onChange={(e) => handleChange("telegram_link", e.target.value)}
                    placeholder="https://t.me/..."
                    className="h-12 border-2 border-gray-200 focus:border-black transition-colors"
                  />
                </div>

                <div>
                  <Label htmlFor="whatsapp_link" className="text-xs text-gray-600">
                    WhatsApp
                  </Label>
                  <Input
                    id="whatsapp_link"
                    value={formData.whatsapp_link}
                    onChange={(e) => handleChange("whatsapp_link", e.target.value)}
                    placeholder="https://wa.me/..."
                    className="h-12 border-2 border-gray-200 focus:border-black transition-colors"
                  />
                </div>
                <div>
                  <Label htmlFor="linkedin_link" className="text-xs text-gray-600">
                    LinkedIn
                  </Label>
                  <Input
                    id="linkedin_link"
                    value={formData.linkedin_link}
                    onChange={(e) => handleChange("linkedin_link", e.target.value)}
                    placeholder="https://linkedin.com/in/..."
                    className="h-12 border-2 border-gray-200 focus:border-black transition-colors"
                  />
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-medium text-gray-700">
                Ad Image (Optional)
              </Label>
              <div className="border-2 border-dashed border-gray-200 rounded-lg p-6">
                {formData.image_url ? (
                  <div className="space-y-3">
                    <img
                      key={formData.image_url}
                      src={`${process.env.REACT_APP_BACKEND_URL}${formData.image_url}?t=${Date.now()}`}
                      alt="Ad preview"
                      className="max-h-48 mx-auto rounded-lg shadow-lg"
                      onLoad={() => console.log('Image loaded successfully')}
                      onError={(e) => {
                        console.error('Image failed to load:', formData.image_url);
                        // Hide the broken image instead of showing error toast
                        e.target.style.display = 'none';
                      }}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleRemoveImage}
                      className="w-full"
                    >
                      Remove Image
                    </Button>
                  </div>
                ) : (
                  <div className="text-center">
                    <Input
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                      disabled={uploading}
                      className="hidden"
                      id="image-upload"
                      data-testid="image-upload-input"
                    />
                    <Label
                      htmlFor="image-upload"
                      className="cursor-pointer inline-flex flex-col items-center"
                    >
                      <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-2">
                        <span className="text-2xl">📷</span>
                      </div>
                      <span className="text-sm text-gray-600">
                        {uploading ? "Uploading..." : "Click to upload image"}
                      </span>
                      <span className="text-xs text-gray-400 mt-1">
                        PNG, JPG, GIF up to 5MB
                      </span>
                    </Label>
                  </div>
                )}
              </div>
            </div>

            <div className="flex gap-4 pt-4">
              <Button
                type="submit"
                data-testid="submit-ad-button"
                disabled={loading}
                className="flex-1 h-12 bg-black text-white hover:bg-gray-800 transition-colors"
              >
                {loading ? "Updating Ad..." : "Update Ad"}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate("/")}
                data-testid="cancel-button"
                className="flex-1 h-12 border-2 border-gray-300 hover:bg-gray-100"
              >
                Cancel
              </Button>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
};

export default EditAd;
